import { forwardRef, useEffect, useMemo, useState } from 'react';
import { Delete, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  composeJapaneseVehiclePlate,
  emptyPlateParts,
  formatSerial,
  JAPANESE_PLATE_REGIONS,
  normalizeClassNo,
  normalizeKana,
  parseJapaneseVehiclePlate,
  type PlateParts,
} from './japanese-vehicle-plate';

const KANA = [
  'あ',
  'い',
  'う',
  'え',
  'か',
  'き',
  'く',
  'け',
  'こ',
  'さ',
  'す',
  'せ',
  'そ',
  'た',
  'ち',
  'つ',
  'て',
  'と',
  'な',
  'に',
  'ぬ',
  'ね',
  'の',
  'は',
  'ひ',
  'ふ',
  'ほ',
  'ま',
  'み',
  'む',
  'め',
  'も',
  'や',
  'ゆ',
  'よ',
  'ら',
  'り',
  'る',
  'れ',
  'ろ',
  'わ',
  'を',
];

interface JapaneseVehiclePlateInputProps {
  id?: string;
  value?: string | null;
  disabled?: boolean;
  onBlur?: () => void;
  onChange: (value: string) => void;
}

export const JapaneseVehiclePlateInput = forwardRef<
  HTMLInputElement,
  JapaneseVehiclePlateInputProps
>(({ id, value, disabled, onBlur, onChange }, ref) => {
  const { t } = useTranslation('common');
  const [parts, setParts] = useState<PlateParts>(() => parseJapaneseVehiclePlate(value));
  const datalistId = useMemo(
    () => `${id ?? 'vehicle-plate'}-regions-${Math.random().toString(36).slice(2)}`,
    [id],
  );

  useEffect(() => {
    setParts(parseJapaneseVehiclePlate(value));
  }, [value]);

  const update = (next: PlateParts) => {
    setParts(next);
    onChange(composeJapaneseVehiclePlate(next));
  };

  const addDigit = (digit: string) => {
    if (parts.serialDigits.length >= 4) return;
    update({ ...parts, serialDigits: `${parts.serialDigits}${digit}` });
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-[1.2fr_0.8fr_0.6fr_1fr]">
        <div>
          <Input
            ref={ref}
            id={id}
            list={datalistId}
            value={parts.region}
            disabled={disabled}
            onBlur={onBlur}
            onChange={(e) => update({ ...parts, region: e.target.value })}
            placeholder={t('efs.common.vehicle_plate.region', { defaultValue: '地域' })}
          />
          <datalist id={datalistId}>
            {JAPANESE_PLATE_REGIONS.map((region) => (
              <option key={region} value={region} />
            ))}
          </datalist>
        </div>
        <Input
          value={parts.classNo}
          disabled={disabled}
          onBlur={onBlur}
          onChange={(e) => update({ ...parts, classNo: normalizeClassNo(e.target.value) })}
          maxLength={3}
          placeholder={t('efs.common.vehicle_plate.class_no', { defaultValue: '分類' })}
        />
        <Input
          value={parts.kana}
          disabled={disabled}
          onBlur={onBlur}
          onChange={(e) => update({ ...parts, kana: normalizeKana(e.target.value) })}
          maxLength={1}
          placeholder={t('efs.common.vehicle_plate.kana', { defaultValue: 'かな' })}
        />
        <Input
          value={formatSerial(parts.serialDigits)}
          disabled={disabled}
          readOnly
          placeholder={t('efs.common.vehicle_plate.serial', { defaultValue: '番号' })}
        />
      </div>

      <div className="rounded-md border border-efs-border bg-efs-bg-subtle p-2">
        <div className="mb-2 flex flex-wrap gap-1">
          {KANA.map((kana) => (
            <Button
              key={kana}
              type="button"
              variant={parts.kana === kana ? 'default' : 'outline'}
              size="sm"
              disabled={disabled}
              className="h-9 min-w-9 px-2 text-base"
              onClick={() => update({ ...parts, kana })}
            >
              {kana}
            </Button>
          ))}
        </div>
        <div className="grid grid-cols-6 gap-1 sm:grid-cols-12">
          {['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'].map((digit) => (
            <Button
              key={digit}
              type="button"
              variant="outline"
              size="sm"
              disabled={disabled || parts.serialDigits.length >= 4}
              className="h-10 text-base"
              onClick={() => addDigit(digit)}
            >
              {digit}
            </Button>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled || parts.serialDigits.length === 0}
            className="h-10"
            onClick={() => update({ ...parts, serialDigits: parts.serialDigits.slice(0, -1) })}
            title={t('efs.common.vehicle_plate.backspace', { defaultValue: '1文字削除' })}
          >
            <Delete className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled || composeJapaneseVehiclePlate(parts) === ''}
            className="h-10"
            onClick={() => update(emptyPlateParts)}
            title={t('efs.common.vehicle_plate.clear', { defaultValue: 'クリア' })}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
});

JapaneseVehiclePlateInput.displayName = 'JapaneseVehiclePlateInput';
